import BaseComponent from '../../base/base_component';
import Model from './message_model';
import Event from '../../utils/event';
import Device from '../../utils/device/device';
import {SYSTEM_EVENTS, USER_EVENTS} from '../../events/events';
import DOM from '../../utils/dom';
import _ from '../../utils/underscore';
import * as MDP_CONSTS from './message_consts';
import {OVERLAY_CONTENT_PROPERTIES} from "../overlay/overlay_const";



// Views
const view = require('./view/message.html');
const titleElTemplate = require('./view/title_template.html');
const BUTTONS_AREA_SELECTOR = 'cgp-btn-area';
const TITLE_SELECTOR = 'cgp-msg-title';
const IMAGE_CONTENT_SELECTOR = 'cgp-msg-content-img';
const CONTENT_WRAPPER = 'cgp-msg-content-wrapper';
const CLOSE_BUTTON_SELECTOR = '.cgp-mdp-close-btn-wrapper';
const CONTENT_AREA = 'cgp-msg-content';
const FOOTER_CONTENT_AREA = 'cgp-msg-footer-content';




/**
 * Generic component to popup CGP messages.
 * Message pops up over overlay and triggered by message_manager_service that subscribed on SYSTEM_EVENTS.MESSAGE_OPEN.
 * So, to pop message, client need to fire MESSAGE_OPEN event with specific message properties (options)
 *
 * @example
 * const messageParams = {
 *  title: 'Simple message title',
 *  text: 'simple message text',
 *  type: 'error'
 * }
 *
 * //fire event to open message
 * Event.fire(SYSTEM_EVENTS.MESSAGE_OPEN, messageParams);
 *
 * @options
 * type {string}            predefined CGP message type. Used for automatically add specified image class to image placeholder
 * title {string}           message title text. It appears on the top of message dialog, but under icon if exists (read type and customIcon)
 * text {string}            message text. Can be every HTML markup. It appears under the title
 * priority {string}        message priority. Since the message opens on overlay, it defines the overlay priority. There are two priority types in CGP (HIGH and LOW).
 *                          default value for messages is PRIORITY_LEVEL.MESSAGE, so when your message will appear it will destroy all previous overlays and messages. If need to change this behaviour,
 *                          you should pass lower priority: PRIORITY_LEVEL.BASE, then your message will appear when the higher overlay destroys.
 *                          NOTE! If you pass priority and there is custom button in message (read button) that should close the message on click, be aware, you should pass priority
 *                          parameter in button onClick callback where you fire CLOSE_OVERLAY event
 *                          @Example
 *                          Event.fire(SYSTEM_EVENTS.CLOSE_OVERLAY, {priority: priorityValue); //priorityValue =  PRIORITY_LEVEL.MESSAGE or PRIORITY_LEVEL.BASE (similar to what passed in message options)
 * closeButton {bool}       define if close X button will appear to close the message
 * buttons {array}          contains buttons on message dialog. Read button object definition in message_button.
 *                          By default, if the array is empty, message dialog render single default button with name 'OK' that close the dialog.
 *                          Each custom button by default closes message dialog. To prevent this behavior, isClosable button parameter will be define false explicitly. (see message_button).
 *                          @Example
 *                          [{name: 'btn1'}, {name: 'btn2', isClosable: false}]
 * buttonPosition {string}  define buttons position. Values: 'left', 'right', 'center'.
 *                          By default, in PC layout, button aligned to center, and in mobile to right side.
 *                          If buttonPosition is defined, it overrides this behaviour and valid both in PC and mobile
 * imgClass {string}        The class contains image url that should be displayed. There is pre-defined placeholder (div) in the left side of the message text area in PC and below the text in Mobile.
 *                          If the property is defined and not empty, even if there no image, the full size placeholder with this (imgClass) class will be allocated to contain an image.
 * hasImage {boolean}       Define image placeholder behavior  as imgClass, but without adding css class to placeholder. Used for future customization of image placeholder.
 * analytic {object}        {category, action, label, value}
 *
 * forceAnalytic (bool)    component sends analytic event automatically on open message dialog. In order to prevent it, value true has to be send. Default = false
 */


class MessageComponent extends BaseComponent {
    /**
     * @static
     * @type {string}
     */
    static CLASS = 'MessageComponent';

    /**
     * Root component selector.
     *
     * @type {string}
     * @private
     */
    _selector = 'cgp-mdp-component';

    /**
     * @property
     * @type {object|null}
     * @private
     */
    _model = null;


    /**
     * @param params
     */
    init(params) {
        super.init(params);
        this._model = Model.create(params);
    }


    /**
     * Inject message content element
     * @private
     */
    _injectContent() {
        const contentArea = this.getDOMElementByClassName(CONTENT_AREA);

        const alignment = this._model.contentAlign || this.serviceFactory.themesService().getTextAlignment();
        let alignmentClass;
        switch (alignment.toLowerCase()) {
            case MDP_CONSTS.TEXT_ALIGNMENT.CENTER:
                alignmentClass = 'cgp-msg-content-center';
                break;
            case MDP_CONSTS.TEXT_ALIGNMENT.LEFT:
                alignmentClass = this.serviceFactory.languageService().getIsRTL() ? 'cgp-msg-content-right' : 'cgp-msg-content-left';
                break;
            case MDP_CONSTS.TEXT_ALIGNMENT.RIGHT:
                alignmentClass = this.serviceFactory.languageService().getIsRTL() ? 'cgp-msg-content-left' : 'cgp-msg-content-right';
                break;
        }

        DOM.addClass(contentArea, alignmentClass);
        DOM.append(contentArea, this._model.contentEl);
    }

    /**
     * Inject message footer content element
     * @private
     */
    _injectFooterContent() {
        if (this._model.footerText) {
            const footerDiv = this.getDOMElementByClassName(FOOTER_CONTENT_AREA);
            DOM.removeClass(footerDiv, 'cgp-hidden');
            DOM.append(footerDiv, this._model.footerContentEl);

            if(this._model.footerClickedCallback && _.isFunction(this._model.footerClickedCallback)){
                this.onChildElementEvent('.' + FOOTER_CONTENT_AREA,
                    USER_EVENTS.ON_CLICK, this._model.footerClickedCallback);
            }
        }
    }

    _injectTitleElements() {
        const titleElements = this._model.titleElements;
        const messageTitle = this.getDOMElementByClassName("cgp-themes-main-title");
        if (titleElements && _.isArray(titleElements)) {
            titleElements.forEach((el) => {
                const bakedHTML = this.bake(titleElTemplate, {
                    titleEl: el.element.outerHTML
                });
                const newTitleEl = DOM.createElement('div', bakedHTML);
                const mouseTargetElement = newTitleEl.querySelector('.cgp-mdp-title-el-wrapper').firstElementChild;

                DOM.addClass(newTitleEl, 'cgp-mdp-title-el-container');
                if (el.customClass) {
                    DOM.addClass(newTitleEl, el.customClass);
                }
                DOM.append(messageTitle, newTitleEl);
                if (el.onClick && _.isFunction(el.onClick)) {
                    this.onDOMEvent(mouseTargetElement, USER_EVENTS.ON_CLICK, el.onClick);
                }
            });
        }
    }


    /**
     * Add buttons to message dialog and initialize their behavior.
     *
     * @private
     */
    _injectButtons() {
        const buttonArea = this.getDOMElementByClassName(BUTTONS_AREA_SELECTOR);
        if (this._model.buttons.length === 0) {
            DOM.removeElement(this.getEl(), buttonArea);
            return;
        }

        const alignment = this._model.buttonPosition;
        // if buttons position is not defined, take text alignment from themes
        if (!_.isNullOrUndefined(alignment) && alignment !== '') {
            // alignment = this.serviceFactory.themesService().getTextAlignment();
            // // upside alignment. If text alignment is left, so button should be in the right side and vice versa
            // alignment = alignment === MDP_CONSTS.TEXT_ALIGNMENT.LEFT ? MDP_CONSTS.TEXT_ALIGNMENT.RIGHT : alignment === MDP_CONSTS.TEXT_ALIGNMENT.RIGHT ? MDP_CONSTS.TEXT_ALIGNMENT.LEFT : alignment;
            // override or define button position
                    //let btnPosition;
            switch (alignment.toLowerCase()) {
                case MDP_CONSTS.TEXT_ALIGNMENT.LEFT:
                    //btnPosition = 'flex-start';
                    DOM.addClass(buttonArea, 'cgp-flex-justify-left');
                    break;
                case MDP_CONSTS.TEXT_ALIGNMENT.RIGHT:
                    //btnPosition = 'flex-end';
                    DOM.addClass(buttonArea, 'cgp-flex-justify-right');
                    break;
                case MDP_CONSTS.TEXT_ALIGNMENT.CENTER:
                    //btnPosition = 'center';
                    DOM.addClass(buttonArea, 'cgp-flex-justify-center');
                    break;
            }
        }


        if (this._model.buttons.length > 1) {
            DOM.addClass(buttonArea, 'cgp-mdp-multiple-btns');
        }

        this._model.buttons.forEach((button, index) => {
            const btnOptions = button.getOptions();
            const htmlButton = DOM.createElement('button', btnOptions.name);

            // atributes
            htmlButton.setAttribute('id', btnOptions.id);
            htmlButton.setAttribute('class', btnOptions.cssClass);

            // append to DOM
            buttonArea.appendChild(htmlButton);

            // attach events
            this.onChildElementEvent('#' + btnOptions.id, USER_EVENTS.ON_CLICK, btnOptions.callback);

            this.onChildElementEvent('#' + btnOptions.id, USER_EVENTS.ON_CLICK, () => {
                // if button is closable, then added event to close the message
                if (btnOptions.isClosable) {
                    this.closeMessage();
                }

                // bind click event listener to each button to send analytic
                if (!btnOptions.forceAnalytic) {
                    Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, Object.assign({
                        category: 'Client_Message',
                        action: 'CTA' + (index + 1),
                        label: btnOptions.name
                    }, this._model.analytic));
                }
            });
        });
    }


    /**
     * If title exists, the function show it in specified title container
     *
     * @private
     */
    _handleMessageTitle() {
        if (this._model.closeButton) {
            DOM.show(this.getEl().querySelectorAll(CLOSE_BUTTON_SELECTOR)[0] as HTMLElement);
            this.onChildElementEvent(CLOSE_BUTTON_SELECTOR, USER_EVENTS.ON_CLICK, () => {
                    if (this._model.btnCloseCallback && _.isFunction(this._model.btnCloseCallback)) {
                        this._model.btnCloseCallback.call();
                    }
                    this.closeMessage();
                }
            );
        }

        if (this._model.title && this._model.hasTitle) {
            const title = this.getEl().getElementsByClassName(TITLE_SELECTOR) as HTMLCollectionOf<HTMLElement>;

            const alignment = this.serviceFactory.themesService().getTextAlignment();
            let alignmentClass;
            switch (alignment.toLowerCase()) {
                case MDP_CONSTS.TEXT_ALIGNMENT.CENTER:
                    break;
                case MDP_CONSTS.TEXT_ALIGNMENT.LEFT:
                    alignmentClass = 'cgp-msg-title-left';
                    break;
                case MDP_CONSTS.TEXT_ALIGNMENT.RIGHT:
                    alignmentClass = 'cgp-msg-title-right';
                    break;

            }
            DOM.addClass(title[0], alignmentClass);
            DOM.show(title[0]);
        }
    }

    /**
     * Set image within text area by message type or imgClass if the message is configured to contain an image
     *
     * @private
     */
    _handleContentImage() {
        if (this._isHasImage()) {
            const imgClass = MDP_CONSTS.MDP_IMG_CSS_CLASS_SELECTORS[this._model.type] || this._model.imgClass;
            const contentWrapper = this.getEl().getElementsByClassName(CONTENT_WRAPPER)[0];
            DOM.addClass(contentWrapper, 'cgp-msg-has-image');
            DOM.addClass(this.getDOMElementByClassName(IMAGE_CONTENT_SELECTOR), imgClass);
            DOM.show(this.getDOMElementByClassName(IMAGE_CONTENT_SELECTOR) as HTMLElement);
        }
    }


    /**
     * Show message in light box
     *
     */
    show() {
        const bakedHTML = this.bake(view);
        this.setHTML(bakedHTML);
        this._injectButtons();
        this._injectContent();
        this._injectFooterContent();
        this._injectTitleElements();
        this._handleMessageTitle();
        this._handleContentImage();

        Event.fire(SYSTEM_EVENTS.OPEN_OVERLAY, {
            component: this,
            priority: this._model.priority,
            isCloseBtnAllowed: false,
            ignoreMobile: this._model.ignoreMobile,
            additionalClasses: this._getAdditionalClasses(),
            contentAdditionalClasses: ['cgp-theme-property-lb-corner-radius', 'cgp-mobile-no-full-screen-overlay'],
            openCallback: () => this._onMessageOpen(),
            closeCallback: () => this._onMessageClosed()
        });


        if(Device.hybrid.isHybrid && this._model.showPopupAboveSecondWebViewHybrid) {
               this.serviceFactory.hybridService().openPopUpOnSecondWebView('.' + OVERLAY_CONTENT_PROPERTIES.CLASS_NAME, 500);
        }
        return '';
    }

    closeMessage() {

        if(Device.hybrid.isHybrid && this._model.showPopupAboveSecondWebViewHybrid) {
            this.serviceFactory.hybridService().closePopUpOnSecondWebView();
        }

        Event.fire(SYSTEM_EVENTS.CLOSE_OVERLAY, {priority: this._model.priority});
    }

    _getAdditionalClasses() {
        const classList = ['cgp-mdp-dialog']
                .concat(this._model.customCSSClassList);

        return classList;
    }



    /**
     * Return indication whether message should contain image.
     * By default hasImage parameter will be assign.
     * Otherwise, if it's predefined message, so type is not empty and there is MDP image mapping in Integration (defined by b2c), then this value will be assign.
     * In any case, if hasImage property is true, the function returns true (TBD)
     *
     * @returns {*}
     * @private
     */
    _isHasImage() {
        let hasImage = this._model.hasImage;
        if (this._model.type && this._model.type !== '') {
            const messageMapper = this.serviceFactory.integrationService().getMDPImageMapper();
            if (messageMapper) {
                hasImage = messageMapper[this._model.type] || hasImage; //TBD or False???
            }
        }

        return hasImage;
    }


    /**
     * when message dialog is opened we have to fire analytic event if any analytic data is existed
     * Currently, not usable, maybe can be used in the future
     *
     * @private
     */
    _onMessageOpen() {
        if (this._model.analytic && !this._model.forceAnalytic) {
            Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, this._model.analytic);
        }
        if (this._model.openCallback && _.isFunction(this._model.openCallback)) {
            this._model.openCallback.call();
        }
    }


    /**
     * Handle close callback
     * @private
     */
    _onMessageClosed() {
        if (this._model.closeCallback && _.isFunction(this._model.closeCallback)) {
            this._model.closeCallback.call();
        }
    }
}

export default MessageComponent;